import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, SortDirection } from '@angular/material/sort';
import { NavigationExtras, Router } from '@angular/router';
import { ResponseStatus } from '@app/core/enums/response-status.enum';
import { LoggerService } from '@app/core/services/logger.service';
import { GridConfiguration } from '@app/shared/models/grid-configuration.model';
import { GridRequestColumn } from '@app/shared/models/grid-request-column.model';
import { GridOrderDirection } from '@app/shared/models/grid-request-order.model';
import { GridRequestService } from '@app/shared/services/grid-request.service';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { formatDate, isNullOrUndefined } from '@app/shared/functions/common.function';
import { GridToggleCommand } from '@app/shared/commands/grid-toggle.command';
import { HttpServerResponse } from '@app/core/models/response.model';
import { MatTableDataSource } from '@angular/material/table';
import { GridRequestCommand } from '@app/shared/commands/grid-request.command';
import { GridColumnType } from '@app/shared/enums/grid-column-type.enum';
import { GridPaginationEntity } from '@app/shared/models/grid-pagination-entity.model';
import { GridColumnConfiguration } from '@app/shared/models/grid-column-configuration.model';
const logger = new LoggerService('GenericGridComponent');

@Component({
  selector: 'dhl-generic-grid',
  templateUrl: './generic-grid.component.html',
  styleUrls: ['./generic-grid.component.scss']
})
export class GenericGridComponent<TSource> implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {
  /** Valores de configuración para el componente */
  @Input() configuration!: GridConfiguration;

  /** Filtros aplicados a obtencion de registros del grid */
  @Input() filters?: unknown;

  /** Evento que se dispara al dar clic sobre el botón de "Edición" */
  @Output() editRow: EventEmitter<TSource> = new EventEmitter();

  /** Evento que se dispara despues del cambio de estatus del registro */
  @Output() toggleRow: EventEmitter<TSource> = new EventEmitter();

  /** Evento que se dispara al dar clic sobre uan fila del grid */
  @Output() clickRow: EventEmitter<TSource> = new EventEmitter();

  /** Evento que se dispara en la selección de una columna por medio de su checkbox */
  @Output() selectRow: EventEmitter<TSource[]> = new EventEmitter();

  /** Evento que se dispara al ocurrir un error en la carga de datos en el Grid */
  @Output() loadDataError: EventEmitter<HttpErrorResponse | Error> = new EventEmitter();

  /** Contiene el paginador del Grid */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Contiene el ordenamiento del Grid */
  @ViewChild(MatSort) sort!: MatSort;

  /** Contiene la definición del checkbox de selección multiple por página del Grid */
  @ViewChild(MatCheckbox) allCheck!: MatCheckbox;

  /** Contiene todas las acciones genericas que contiene el grid */
  @ContentChild(TemplateRef) actionsRef!: TemplateRef<this>;

  /** Evento que controla el camnbio de los filtros aplicados al grid */
  filterEvent: EventEmitter<void> = new EventEmitter();

  dataSource: TSource[] = [];
  dataSourceLocal = new MatTableDataSource<TSource>([]);
  displayedColumns: string[] = [];
  selection = new SelectionModel<TSource>(true, []);
  totalRecords = 0;
  gridColumnTypeEnum = GridColumnType;
  hasDataAccess: boolean = true;

  fnFormatDate = formatDate;

  constructor(
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly cdRef: ChangeDetectorRef,
    private readonly gridRequestService: GridRequestService
  ) {}

  ngOnInit() {
    this.setColumnsToDisplay();
  }

  ngAfterViewInit(): void {
    this.setConfiguration();
    this.onLoadRecords();
    this.cdRef.detectChanges();
  }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges();
  }

  /**
   * Lanza el evento del filtro al detectar un cambio sobre este.
   *
   * @param _changes - Cambios sobre el elemento.
   */
  ngOnChanges(_changes: { [propKey: string]: SimpleChange }) {
    this.filterEvent.emit();
  }

  /**
   * Evento que se dispara al dar clic en el botón de edición de una fila del Grid.
   *
   * @param row - Objeto de la fila seleccionada.
   */
  onEditRow(row: TSource): void {
    if (this.configuration.isEditable) {
      if (this.configuration.editUrl === '') {
        this.editRow.emit(row);
      } else {
        const navigateTo = this.configuration.editUrl;
        const navigateData: NavigationExtras = {
          state: {
            data: row
          }
        };
        void this.router.navigate([navigateTo], { state: navigateData });
      }
    }
  }

  /**
   * Evento que se dispara cuando se da clic sobre el control de activación y desactivación.
   *
   * @param event - Evento del control MatSlide.
   * @param row - Objeto de la fila seleccionada.
   */
  onToggle(row: TSource): void {
    let confirmMsg: string = `¿Está seguro que desea ${!(row[this.configuration.toggleField! as keyof typeof row] as boolean) ? 'activar' : 'desactivar'}
       ${this.configuration.toggleInfoModal?.sustantive} ${this.configuration.toggleInfoModal?.type} ${
      this.configuration.toggleInfoModal?.thereName ? (row['name'! as keyof typeof row] as string) : ''
    }?`;

    confirmMsg += ` ${!(row[this.configuration.toggleField! as keyof typeof row] as boolean) ? '' : this.configuration.toggleInfoModal?.msgToDisabled} `;
    const titleMsg: string = `${!(row[this.configuration.toggleField! as keyof typeof row] as boolean) ? 'Activar' : 'Desactivar'} ${this.configuration.toggleInfoModal?.type}`;
    const labelAcceptt: string = `Si, ${!(row[this.configuration.toggleField! as keyof typeof row] as boolean) ? 'activar' : 'desactivar'} ${
      this.configuration.toggleInfoModal?.type
    }`;
    const confirmDialog = this.dialog.open(GenericDialogComponent, {
      data: {
        title: titleMsg,
        message: confirmMsg,
        acceptLabel: labelAcceptt,
        showAccept: true,
        showCancel: 'Cancelar'
      }
    });

    confirmDialog.afterClosed().subscribe((accept: boolean) => {
      if (accept) {
        const command: GridToggleCommand = this.getToggleCommand(row);
        this.gridRequestService.changeToggleValue(this.configuration.toggleUrl!, command).subscribe({
          next: (response: HttpServerResponse<unknown>) => {
            if (response.status === ResponseStatus.Ok) {
              (row[this.configuration.toggleField! as keyof typeof row] as boolean) = command.status;
              this.snackBar.open(`${response.description}`);
              this.toggleRow.emit(row);
            } else {
              this.snackBar.open(`${response.description}`);
            }
          },
          error: (error: HttpErrorResponse) => {
            logger.error(`Toggle value error: ${error.error}`);
            this.loadDataError.emit(error);
          },
          complete: () => {
            logger.info('Toggle value complete');
          }
        });
      }
    });
  }

  onChangeToggle(event: MatSlideToggleChange, row: TSource): void {
    event.source.checked = row[this.configuration.toggleField as keyof typeof row] as boolean;
  }

  /**
   * Evento que se dispara al realizar un clic sobre una fila del Grid.
   *
   * @param row - Objeto de la fila seleccionada.
   */
  onClickRow(row: TSource): void {
    if (this.configuration.isClickable) {
      this.clickRow.emit(row);
    }
  }

  /**
   * Obtiene el objeto para el cambio de estatus de un registro.
   *
   * @param row - Información de la fila a extraer.
   */
  getToggleCommand(row: TSource): GridToggleCommand {
    const command: GridToggleCommand = {
      id: row[this.configuration.rowId as keyof typeof row] as number | string,
      status: !(row[this.configuration.toggleField as keyof typeof row] as boolean)
    };

    return command;
  }

  /**
   * Valida si, el número de elementos seleccionados coincide con el número total de filas.
   *
   * @returns Valor booleando
   */
  onAllPageSelected(): boolean {
    const allSelected: string[] = this.selection.selected.map((row: TSource) => row[this.configuration.rowId as keyof typeof row] as string);
    let currentPageRows: string[] = [];
    if (this.configuration.request?.isPaginated) {
      currentPageRows = this.dataSource.map((row: TSource) => row[this.configuration.rowId as keyof typeof row] as string);
    } else {
      currentPageRows = this.dataSourceLocal.data.map((row: TSource) => row[this.configuration.rowId as keyof typeof row] as string);
    }
    const pageSelected: number = allSelected.filter((id) => currentPageRows.includes(id)).length;

    return currentPageRows.every((id) => allSelected.includes(id)) && pageSelected === currentPageRows.length;
  }

  /** Selecciona todas las fila de una página. */
  onSwitchAllPage(event: MatCheckboxChange) {
    if (this.configuration.request?.isPaginated) {
      this.dataSource.forEach((row) => {
        const found = this.selection.selected.find((e: TSource) => e[this.configuration.rowId as keyof typeof e] === row[this.configuration.rowId as keyof typeof row]);
        if (event.checked && found === undefined) {
          this.selection.select(row);
        }

        if (found !== undefined && !event.checked) {
          this.selection.deselect(found);
        }
      });
    } else {
      this.dataSourceLocal.data.forEach((row) => {
        const found = this.selection.selected.find((e: TSource) => e[this.configuration.rowId as keyof typeof e] === row[this.configuration.rowId as keyof typeof row]);
        if (event.checked && found === undefined) {
          logger.debug(row);
          this.selection.select(row);
        }

        this.validateForDeselect(found, event);
      });
    }
  }

  validateForDeselect(found: any, event: MatCheckboxChange) {
    if (found !== undefined && !event.checked) {
      this.selection.deselect(found);
    }
  }

  /** Ruta hacia donde navega despues de deteriminada acción dentro del grid */
  onNavigateToUrl() {
    void this.router.navigate([this.configuration.navigation!.navigateTo]);
  }

  /**
   * Evento de multiseleccion de elementos
   *
   * @param row - Fila a seleccionar
   */
  onToSelect(row: TSource) {
    const found = this.selection.selected.find((e: TSource) => e[this.configuration.rowId as keyof typeof e] === row[this.configuration.rowId as keyof typeof row]);
    if (found !== undefined) {
      this.selection.deselect(found);
    } else {
      this.selection.select(row);
    }
  }

  /**
   * Valida si una fila esta seleccioanda
   *
   * @param row - Fila a validar
   * @returns Resultado de la validacion
   */
  isSelected(row: TSource) {
    return this.selection.selected.some((e: TSource) => e[this.configuration.rowId as keyof typeof e] === row[this.configuration.rowId as keyof typeof row]);
  }

  /**
   * Realiza nuevamente la carga de información para actualizar elementos desde el componente "PADRE"
   */
  refreshData(): void {
    this.onLoadRecords();
  }

  /**
   * Limpia la lista de los elementos seleccionados en el Grid
   */
  onClearSelected(): void {
    this.selection.clear();
  }

  /**
   * Configura las columnas que se mostraran en el Grid
   */
  private setColumnsToDisplay(): void {
    if (this.configuration.isSelectable) {
      this.configuration.columns.unshift({
        name: 'select',
        displayName: '',
        isSearchable: false,
        isSortable: false,
        search: {
          regex: '',
          value: ''
        }
      });
    }

    if (this.configuration.isToggleable) {
      this.configuration.columns.push({
        name: 'toggle',
        displayName: 'Estatus',
        isSearchable: false,
        isSortable: false,
        search: {
          regex: '',
          value: ''
        }
      });
    }

    if (this.configuration.hasActions) {
      this.configuration.columns.push({
        name: 'actions',
        displayName: 'Acciones',
        isSearchable: false,
        isSortable: false,
        search: {
          regex: '',
          value: ''
        }
      });
    }

    // Se obtienen las columnas a mostrar en el grid de los parámetros de configuración.
    this.displayedColumns = this.configuration.columns.map((column) => column.name);
  }

  /**
   * Carga de configuración desde un solo punto.
   */
  private setConfiguration(): void {
    this.dataSourceLocal.paginator = this.paginator;

    if (!isNullOrUndefined(this.configuration.order)) {
      this.sort.sort({ id: this.displayedColumns[this.configuration.order?.column as number], start: this.configuration.order?.direction as SortDirection, disableClear: false });
    }
    /** Se inicia el evento de selección de checks. */
    this.selection.changed.subscribe((_event) => {
      this.selectRow.emit(this.selection.selected);
    });

    /** Si se cambia el orden por columna, se hace regresa a la primera página el `paginador`.  */
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.onReloadRecords();
    });

    /** Evento que realiza la petición paginada al seleccionar otro número de pagina */
    this.paginator.page.subscribe(() => {
      this.onReloadRecords();
    });

    /** Evento que se ejecuta al realizar un cambio en los filtros */
    this.filterEvent.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.onReloadRecords();
    });
  }

  /**
   * Realiza la petición a servidor y obtiene los datos para la tabla
   */
  private onLoadRecords(): void {
    if (this.configuration.isRemoteData) {
      const command = this.getGridRequestCommand();
      if (this.configuration.request?.isPaginated) {
        this.getPaginatedData(command);
      } else {
        this.getNoPaginatedData(command);
      }
    } else {
      this.totalRecords = this.configuration.staticData!.length;
      this.dataSourceLocal.data = [];
      this.dataSourceLocal.data = this.configuration.staticData! as TSource[];
      logger.info('Load local records complete');
    }
  }

  private getPaginatedData(command: GridRequestCommand): void {
    this.gridRequestService.getGridData<GridPaginationEntity<TSource>>(this.configuration.request!, command).subscribe({
      next: (response: HttpServerResponse<GridPaginationEntity<TSource>>) => {
        if (response.status === ResponseStatus.Ok) {
          this.totalRecords = response.data!.metaData.totalItemCount;
          this.dataSource = [];
          this.dataSource = response.data!.items;
        } else {
          this.snackBar.open(response.description);
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.Forbidden) {
          logger.error(`Load paginated data by permissions`, error);
          this.hasDataAccess = false;
        } else {
          logger.error(`Load paginated data error:`, error);
        }

        this.loadDataError.emit(error);
      },
      complete: () => {
        logger.info('Load paginated data complete');
      }
    });
  }

  private getNoPaginatedData(command: GridRequestCommand): void {
    this.gridRequestService.getGridData<TSource[]>(this.configuration.request!, command).subscribe({
      next: (response: HttpServerResponse<TSource[]>) => {
        if (response.status === ResponseStatus.Ok) {
          this.totalRecords = response.data!.length;
          this.dataSourceLocal.data = [];
          this.dataSourceLocal.data = response.data!;
        } else {
          this.snackBar.open(response.description);
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.Forbidden) {
          logger.error(`Load no paginated data by permissions`, error);
          this.hasDataAccess = false;
        } else {
          logger.error(`Load no paginated data error:`, error);
        }
        this.loadDataError.emit(error);
      },
      complete: () => {
        logger.info('Load no paginated data complete');
      }
    });
  }

  private onReloadRecords(): void {
    if (this.configuration.request?.isPaginated) {
      this.onLoadRecords();
    }
  }

  /**
   * Obtiene el objeto para la paginación del Grid.
   */
  private getGridRequestCommand(): GridRequestCommand {
    // constt currentColumn g=t thisf.displayedColumns.indextOf(this.sort.active); commend
    const requestData: GridRequestCommand = {
      columns: this.getGridColumns(),
      pageSize: this.paginator.pageSize || 10,
      pageNumber: this.paginator.pageIndex + 1,
      searchOperator: 'or',
      order: {
        column: this.configuration.order!.column ?? 0,
        // column: currentColumn === -1 ? 0 : this.configuration.isSelectable ? currentColumn - 1 : currentColumn,
        direction: this.sort.direction as GridOrderDirection
      }
    };

    if (this.filters !== null && this.filters !== undefined) {
      for (const [key, value] of Object.entries(this.filters)) {
        requestData[key] = value;
      }
    }

    return requestData;
  }

  /**
   * Obtiene la definición de las columnas para el objeto para realizar la paginación del Grid.
   */
  private getGridColumns(): GridRequestColumn[] {
    const columns: GridRequestColumn[] = [];
    for (const column of this.configuration.columns) {
      if (!(column.name === 'select' || column.name === 'actions' || column.name === 'toggle')) {
        const col: GridRequestColumn = {
          name: column.name,
          isSortable: column.isSortable,
          isSearchable: column.isSearchable,
          search: {
            regex: '',
            value: ''
          }
        };

        columns.push(col);
      }
    }

    return columns;
  }

  /**
   * Valida si un determinado elemento, se encuentra fuera del area de pintado de la celda
   *
   * @param e - Elemento a evaluar
   * @returns Validación de overflow
   */
  isOverflowing(e: HTMLElement, parent: HTMLElement): boolean {
    const isOverflowing = e.offsetWidth < e.scrollWidth;
    if (isOverflowing) {
      parent.style.cursor = 'help';
    } else {
      parent.style.cursor = 'default';
    }
    return isOverflowing;
  }

  getColumnTooltip(configuration: GridColumnConfiguration, row: TSource) {
    let tooltip = '';
    switch (configuration.type) {
      case GridColumnType.HTML: {
        const htmlStr = configuration.renderHTML?.(row) as string;
        const parser = new DOMParser();
        const document = parser.parseFromString(htmlStr, 'text/html');
        tooltip = document.body.innerText;
        break;
      }
      case GridColumnType.DateTime:
        tooltip = formatDate(row[configuration.name as keyof typeof row] as string);
        break;
      case GridColumnType.Text:
      case undefined:
      default:
        tooltip = row[configuration.name as keyof typeof row] as string;
        break;
    }
    return tooltip;
  }
}
